import { useState, useCallback, useEffect, useContext } from 'react';
import {
    Box,
    Grid,
    Paper,
    Typography,
    Stack,
    IconButton,
    TextField,
    CircularProgress,
    InputAdornment,
    Backdrop,
    Skeleton
} from '@mui/material';

import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import Swal from 'sweetalert2';

import CustomButton from '../components/custom_components/CustomButton';
import CustomSearch from '../components/custom_components/CustomSearch';
import CustomModalHeader from '../components/custom_components/CustomModalHeader';

import { UserContext } from '../context/UserContext';

const apiUrl = process.env.REACT_APP_API_URL;

// ─── Add / Edit Dialog ───────────────────────────────────────────────────────
function CategoryDialog({ open, onClose, onSave, initial }) {

    const isEdit = !!initial?.id;

    const [name, setName] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        setName(initial?.ItemCategory ?? '');
        setError('');
    }, [initial, open]);

    const handleSave = async () => {
        if (!name.trim()) { setError('Category name is required.'); return; }

        setSaving(true);

        try {

            const response = await fetch(`${apiUrl}manage_category/save_category.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    category: name
                })
            });

            const result = await response.json();

            if (result.status === 'success') {

                onSave({ id: initial?.id ?? Date.now(), ItemCategory: name.trim() });
                onClose();

            } else if (result.status === 'error') {
                Swal.fire({ text: result.message, icon: "error" });
            }

        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Backdrop
            aria-hidden="false"
            open={open}
            sx={{
                zIndex: (theme) => theme.zIndex.drawer + 1,
                backdropFilter: "blur(1px)",
            }}
        >
            <Paper
                sx={{
                    width: 420,
                    p: 3,
                    borderRadius: 2,

                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "4px",
                        background: "#2e3489"
                    }
                }}
            >
                {/* HEADER */}
                <CustomModalHeader
                    title={isEdit ? "Edit Category" : "New Category"}
                    subtitle={isEdit
                        ? "Update the category info"
                        : "Add a new order category"}
                    onClose={onClose}
                />

                {/* BODY */}
                <Box>
                    <TextField
                        fullWidth
                        autoFocus
                        size="small"
                        label="Category Name"
                        value={name}
                        onChange={(e) => {
                            setName(e.target.value);
                            setError("");
                        }}
                        error={!!error}
                        helperText={error}
                        placeholder="e.g. Watch"
                        onKeyDown={(e) => {
                            if (e.key === "Enter") handleSave();
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LabelOutlinedIcon
                                        sx={{
                                            fontSize: 18,
                                            color: "#94a3b8",
                                        }}
                                    />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                {/* FOOTER */}
                <Stack
                    direction="row"
                    justifyContent="flex-end"
                    spacing={2}
                    mt={4}
                >

                    <CustomButton
                        label="Cancel"
                        variant="outlined"
                        hoverColor="#d14249"
                        onClick={onClose}
                    />

                    <CustomButton
                        label={isEdit ? "Save Changes" : "Add Category"}
                        disabled={saving || !name.trim()}
                        onClick={handleSave}
                    />

                </Stack>
            </Paper>

        </Backdrop>
    );
}

// ─── Category Card ───────────────────────────────────────────────────────────
function CategoryCard({ category, onEdit, onDelete, deleting }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                borderRadius: 1.2,
                border: '1px solid #e5e7eb',
                backgroundColor: '#fff',
                transition: 'all 0.2s ease',

                '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                    borderColor: '#d1d5db',
                },
            }}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>

                {/* Icon + Name */}
                <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                >
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            border: "1px solid #e5e7eb",
                            backgroundColor: "#fafafa",

                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",

                            flexShrink: 0,
                        }}
                    >
                        <CategoryOutlinedIcon
                            sx={{
                                fontSize: 20,
                                color: "#555",
                            }}
                        />
                    </Box>

                    <Typography
                        sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: "#111827",
                            lineHeight: 1.3,
                        }}
                    >
                        {category.ItemCategory}
                    </Typography>
                </Stack>

                {/* Actions */}
                <Stack direction="row" spacing={0.5}>

                    {deleting ? (
                        <Box sx={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CircularProgress size={13} sx={{ color: '#DC2626' }} />
                        </Box>
                    ) : (
                        <IconButton
                            size="small"
                            onClick={() => onDelete(category.id)}
                            sx={{
                                width: 34, height: 34,
                                border: '1px solid #E2E8F0',
                                borderRadius: '7px',
                                color: '#DC2626',
                                '&:hover': { bgcolor: '#FEF2F2', borderColor: '#FECACA' },
                            }}
                        >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    )}
                </Stack>
            </Stack>
        </Paper>
    );
}

// ─── Category Card Skeleton ──────────────────────────────────────────────────
function CategoryCardSkeleton() {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                borderRadius: 1.2,
                border: "1px solid #e5e7eb",
                backgroundColor: "#fff",
            }}
        >
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
            >
                {/* Icon + Name */}
                <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                >
                    {/* Icon Skeleton */}
                    <Skeleton
                        variant="rounded"
                        width={40}
                        height={40}
                        sx={{
                            borderRadius: 2,
                            flexShrink: 0,
                        }}
                    />

                    {/* Category Name Skeleton */}
                    <Skeleton
                        variant="text"
                        width={80}
                        height={22}
                        sx={{
                            transform: "none",
                        }}
                    />
                </Stack>

                {/* Action Skeleton */}
                <Skeleton
                    variant="rounded"
                    width={34}
                    height={34}
                    sx={{
                        borderRadius: "7px",
                        flexShrink: 0,
                    }}
                />
            </Stack>
        </Paper>
    );
}

// ─── Main Page ───────────────────────────────────────────────────────────────
export default function ManageCategories() {

    const { categories, setCategories } = useContext(UserContext);

    const [loading, setLoading] = useState(false);

    const [deletingId, setDeletingId] = useState(null);

    const [search, setSearch] = useState("");

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    // ── Fetch ──────────────────────────────────────────────────────────────
    const handleFetch = useCallback(async () => {

        setLoading(true);

        try {
            const response = await fetch(
                `${apiUrl}manage_category/get_categories.php`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                });

            const result = await response.json();

            if (result.status === "success") {
                setCategories(result.data || []);
            } else {
                Swal.fire({
                    text: result.message,
                    icon: "info",
                });
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { handleFetch(); }, [handleFetch]);

    // ── Save (add / edit) ──────────────────────────────────────────────────
    const handleSave = (cat) => {
        setCategories((prev) =>
            prev.find((c) => c.id === cat.id)
                ? prev.map((c) => c.id === cat.id ? cat : c)
                : [cat, ...prev]
        );
    };

    // ── Delete ─────────────────────────────────────────────────────────────
    const handleDelete = async (id) => {
        const res = await Swal.fire({
            title: 'Delete Category?',
            text: 'This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#e94d4d',
            confirmButtonText: 'Delete',
        });

        if (!res.isConfirmed) return;

        setDeletingId(id);

        try {
            const response = await fetch(`${apiUrl}manage_category/delete_category.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ id }),
            });

            const result = await response.json();

            if (result.status === "success") {

                setCategories((prev) => prev.filter((c) => c.id !== id));

            } else {
                Swal.fire({
                    text: result.message,
                    icon: "error",
                });
            }

        } catch (e) {
            console.error(e);
        } finally {
            setDeletingId(null);
        }
    };

    const filtered = categories.filter((c) =>
        c.ItemCategory.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <>
            {/* ── Header ── */}
            <Box mb={2}>
                <Typography fontWeight={700} fontSize={16} color="#111">
                    Categories
                </Typography>
                <Typography fontSize={13} color="#94A3B8">
                    {categories.length} categor{categories.length !== 1 ? 'ies' : 'y'} configured
                </Typography>
            </Box>

            {/* ── Search ── */}
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
            >
                <CustomSearch
                    md="36ch"
                    placeholder="Search by Category"
                    value={search}
                    onChange={(val) => setSearch(val)}
                />

                <CustomButton
                    label="Add"
                    startIcon={<AddIcon />}
                    onClick={() => { setEditing(null); setDialogOpen(true); }}
                />
            </Stack>

            {/* ── Grid ── */}
            {loading ? (

                <Grid container spacing={2}>
                    {[...Array(6)].map((_, index) => (
                        <Grid key={index}  >
                            <CategoryCardSkeleton />
                        </Grid>
                    ))}
                </Grid>

            ) : filtered.length === 0 ? (

                <Stack alignItems="center" justifyContent="center" sx={{ height: 260 }} spacing={1.5}>
                    <Box sx={{
                        width: 56, height: 56, borderRadius: '14px',
                        bgcolor: '#F1F5F9',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <CategoryOutlinedIcon sx={{ fontSize: 26, color: '#CBD5E1' }} />
                    </Box>
                    <Box textAlign="center">
                        <Typography fontWeight={600} color="#475569" fontSize={14}>No categories found</Typography>
                        <Typography fontSize={12} color="#94A3B8" mt={0.5}>
                            {search ? 'Try a different search term' : 'Click "Add" to create your first category'}
                        </Typography>
                    </Box>
                </Stack>

            ) : (

                <Grid container spacing={2}>
                    {filtered.map((cat, i) => (
                        <Grid key={cat.id}>
                            <CategoryCard
                                category={cat}
                                onEdit={(c) => { setEditing(c); setDialogOpen(true); }}
                                onDelete={handleDelete}
                                deleting={deletingId === cat.id}
                            />
                        </Grid>
                    ))}
                </Grid>

            )}

            {/* ── Dialog ── */}
            <CategoryDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSave={handleSave}
                initial={editing}
            />
        </>
    );
}



